import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useSaveInvoiceBatchMutation,
  type InvoiceBatchRowIn,
} from '@/store/api/procurementApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  contractNo: string
  contractDate: string | null
  onClose: () => void
  onSaved: () => void
}

/** Một dòng grid nhập — number giữ raw string để user gõ tự do. */
interface DraftRow {
  warehouse: string
  invoice_no: string
  /** 'YYYY-MM-DD' */
  invoice_date: string
  quantity: string
  price_vnd_kg: string
  total_amount: string
  vat_amount: string
  description: string
}

const emptyRow = (): DraftRow => ({
  warehouse: '',
  invoice_no: '',
  invoice_date: '',
  quantity: '',
  price_vnd_kg: '',
  total_amount: '',
  vat_amount: '',
  description: '',
})

const isRowEmpty = (r: DraftRow): boolean =>
  Object.values(r).every((v) => v.trim() === '')

/** Parse raw input → number | null (blank/garbage → null, mirror Access Null). */
const toNum = (raw: string): number | null => {
  const t = raw.trim()
  if (t === '') return null
  const n = Number(t.replace(/,/g, ''))
  return Number.isFinite(n) ? n : null
}

/** Format numeric hiển thị preview: thousands sep, up to 2 decimals. */
const fmtNum = (value: number | null): string => {
  if (value === null) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** Format date DD-MMM-YYYY (Access display). */
const fmtDate = (value: string | null): string => {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('DD-MMM-YYYY') : ''
}

/** Preview row = query ProcurementPay_WriteInvoice (computed 2 cột). */
interface PreviewRow {
  warehouse: string
  invoice_no: string
  invoice_date: string
  quantity: number | null
  price_vnd_kg: number | null
  total_cal: number | null
  vat_amount: number | null
  invoice_amount_after_tax: number
  description: string
}

const toPreview = (r: DraftRow): PreviewRow => {
  const quantity = toNum(r.quantity)
  const price = toNum(r.price_vnd_kg)
  const totalInput = toNum(r.total_amount)
  const vat = toNum(r.vat_amount)
  // Total Amount (CAL) = Total Amount nếu nhập, else ROUND(price × quantity).
  const totalCal =
    totalInput !== null
      ? totalInput
      : price !== null && quantity !== null
        ? Math.round(price * quantity)
        : null
  return {
    warehouse: r.warehouse,
    invoice_no: r.invoice_no,
    invoice_date: r.invoice_date,
    quantity,
    price_vnd_kg: price,
    total_cal: totalCal,
    vat_amount: vat,
    // Invoice Amount (after tax) = Nz(Total Amount (CAL),0) + Nz(VAT Amount,0).
    invoice_amount_after_tax: (totalCal ?? 0) + (vat ?? 0),
    description: r.description,
  }
}

const headCellSx = {
  fontWeight: 600,
  fontSize: '0.72rem',
  whiteSpace: 'nowrap',
  py: 0.5,
  px: 0.75,
} as const

const bodyCellSx = { py: 0.5, px: 0.75, fontSize: '0.8125rem' } as const

/**
 * Contract Detail → Add Invoice dialog — converts Access
 * `frmProcurementQryDetailsAddInvoice` (caption "Procurement - Invoice").
 *
 * Batch nhập nhiều dòng invoice cho 1 hợp đồng (P-* / NPE-*): grid nhập
 * (Warehouse / Invoice No / Invoice Date / Quantity / Price / Total Amount /
 * VAT Amount / Description) + grid preview read-only với 2 cột computed
 * (Total Amount (CAL), Invoice Amount (after tax)) tính realtime client-side.
 * Save gọi POST invoice-batch — BE validate exact Access messages rồi insert
 * từng dòng vào một transaction. No fullScreen.
 */
export default function ContractDetailAddInvoiceDialog({
  open,
  contractNo,
  contractDate,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const [saveBatch, { isLoading: isSaving }] = useSaveInvoiceBatchMutation()

  const [rows, setRows] = useState<DraftRow[]>([emptyRow()])

  // Re-init each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setRows([emptyRow()])
  }, [open])

  const setCell = (idx: number, key: keyof DraftRow, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)))
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])

  const removeRow = (idx: number) =>
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx)
      return next.length > 0 ? next : [emptyRow()]
    })

  // Preview computed realtime (Access cần bấm "Refresh to Check" để requery).
  const preview = useMemo(
    () => rows.filter((r) => !isRowEmpty(r)).map(toPreview),
    [rows],
  )

  const handleRefresh = () => {
    // Preview đã realtime — nút giữ cho khớp layout Access.
    snack.info(`Preview refreshed — ${preview.length} row(s)`)
  }

  const handleSave = async () => {
    const filled = rows.filter((r) => !isRowEmpty(r))
    if (filled.length === 0) {
      snack.warning('Please input at least one Invoice row !')
      return
    }
    const payload: InvoiceBatchRowIn[] = filled.map((r) => ({
      warehouse: r.warehouse.trim() || null,
      invoice_no: r.invoice_no.trim() || null,
      invoice_date: r.invoice_date || null,
      quantity: toNum(r.quantity),
      price_vnd_kg: toNum(r.price_vnd_kg),
      total_amount: toNum(r.total_amount),
      vat_amount: toNum(r.vat_amount),
      description: r.description.trim() || null,
    }))
    try {
      const res = await saveBatch({ contract_no: contractNo, rows: payload }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Errors: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save invoices'))
    }
  }

  const inputCell = (
    idx: number,
    key: keyof DraftRow,
    opts?: { numeric?: boolean; width?: number },
  ) => (
    <TextField
      size="small"
      fullWidth
      value={rows[idx][key]}
      onChange={(e) => setCell(idx, key, e.target.value)}
      disabled={isSaving}
      sx={{ minWidth: opts?.width ?? 110, ...(opts?.numeric ? numSx : null) }}
    />
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Procurement - Invoice" sx={dialogTitleTextSx}>
          Inbound Logistics - Invoice
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* Context row: Contract No | Date (read-only). */}
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            mb: 2,
          }}
        >
          <TextField
            label="Contract No"
            size="small"
            fullWidth
            value={contractNo}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={roSx}
          />
          <TextField
            label="Date"
            size="small"
            fullWidth
            value={fmtDate(contractDate)}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={roSx}
          />
        </Box>

        <Typography sx={{ ...sectionHeaderSx, mb: 1 }}>
          Input data in the below form
        </Typography>

        <Box sx={{ overflowX: 'auto', mb: 1 }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Warehouse</TableCell>
                <TableCell sx={headCellSx}>Invoice No</TableCell>
                <TableCell sx={headCellSx}>Invoice Date</TableCell>
                <TableCell sx={headCellSx} align="right">Quantity</TableCell>
                <TableCell sx={headCellSx} align="right">Price (VND/KG)</TableCell>
                <TableCell sx={headCellSx} align="right">Total Amount</TableCell>
                <TableCell sx={headCellSx} align="right">VAT Amount (INPUT)</TableCell>
                <TableCell sx={headCellSx}>Description</TableCell>
                <TableCell sx={headCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'warehouse')}</TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'invoice_no', { width: 130 })}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <DatePicker
                      format="DD-MMM-YYYY"
                      disabled={isSaving}
                      value={row.invoice_date ? dayjs(row.invoice_date) : null}
                      onChange={(d) =>
                        setCell(
                          idx,
                          'invoice_date',
                          d && d.isValid() ? d.format('YYYY-MM-DD') : '',
                        )
                      }
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 165 } },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'quantity', { numeric: true })}</TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'price_vnd_kg', { numeric: true })}</TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'total_amount', { numeric: true, width: 120 })}</TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'vat_amount', { numeric: true, width: 120 })}</TableCell>
                  <TableCell sx={bodyCellSx}>{inputCell(idx, 'description', { width: 160 })}</TableCell>
                  <TableCell sx={bodyCellSx} padding="none">
                    <IconButton
                      size="small"
                      onClick={() => removeRow(idx)}
                      disabled={isSaving}
                      aria-label="Remove row"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={isSaving}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Add row
        </Button>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={sectionHeaderSx}>
            Check data in the below form before click "SAVE"
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Refresh to Check
          </Button>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Warehouse</TableCell>
                <TableCell sx={headCellSx}>Invoice No</TableCell>
                <TableCell sx={headCellSx}>Invoice Date</TableCell>
                <TableCell sx={headCellSx} align="right">Quantity</TableCell>
                <TableCell sx={headCellSx} align="right">Price (VND/KG)</TableCell>
                <TableCell sx={headCellSx} align="right">Total Amount (CAL)</TableCell>
                <TableCell sx={headCellSx} align="right">VAT Amount</TableCell>
                <TableCell sx={headCellSx} align="right">Invoice Amount (after tax)</TableCell>
                <TableCell sx={headCellSx}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ ...bodyCellSx, color: 'text.secondary' }}>
                    No data to check
                  </TableCell>
                </TableRow>
              ) : (
                preview.map((p, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={bodyCellSx}>{p.warehouse}</TableCell>
                    <TableCell sx={bodyCellSx}>{p.invoice_no}</TableCell>
                    <TableCell sx={bodyCellSx}>{fmtDate(p.invoice_date || null)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{fmtNum(p.quantity)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{fmtNum(p.price_vnd_kg)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{fmtNum(p.total_cal)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{fmtNum(p.vat_amount)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{fmtNum(p.invoice_amount_after_tax)}</TableCell>
                    <TableCell sx={bodyCellSx}>{p.description}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
